/**
 * Chat Widget Component
 * Floating chat interface for AI assistance
 * Updated for unified architecture with multi-mode support (Navigate, Guide, Agent)
 */

import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { WorkflowPlayerService } from '../guide/workflow-player.service';
import { AiAssistApiService } from '../services/ai-assist-api.service';
import { IntentClassifierService } from '../core/intent-classifier.service';
import { ActionExecutorService } from '../core/action-executor.service';
import { ChatMessage, WorkflowSession } from '../models/workflow.models';
import { ClassifiedIntent } from '../models/app-knowledge.models';

@Component({
  selector: 'ai-chat-widget',
  templateUrl: './chat-widget.component.html',
  styleUrls: ['./chat-widget.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule]
})
export class ChatWidgetComponent implements OnInit, OnDestroy {

  @Input() position: 'bottom-right' | 'bottom-left' = 'bottom-right';
  @Input() quickSuggestions: string[] = [
    'How do I import contacts?',
    'Export call metrics',
    'Create a new deal'
  ];

  // UI state
  isExpanded = false;
  isMinimized = false;
  isLoading = false;
  showSuggestions = true;

  // Chat state
  messages: ChatMessage[] = [];
  userInput = '';

  // Workflow state
  activeSession: WorkflowSession | null = null;
  currentStep = 0;
  totalSteps = 0;

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private workflowPlayer: WorkflowPlayerService,
    private apiService: AiAssistApiService,
    private intentClassifier: IntentClassifierService,
    private actionExecutor: ActionExecutorService
  ) {}

  ngOnInit(): void {
    // Subscribe to workflow player state
    this.workflowPlayer.activeSession$
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        this.activeSession = session;

        if (session) {
          this.currentStep = session.currentStepIndex + 1;
          // Total steps is set when workflow starts
        }

        if (session?.status === 'completed') {
          this.addMessage('assistant', 'Workflow completed successfully!');
          this.showSuggestions = true;
        } else if (session?.status === 'cancelled') {
          this.addMessage('system', 'Workflow cancelled.');
          this.showSuggestions = true;
        }
      });

    this.workflowPlayer.currentStep$
      .pipe(takeUntil(this.destroy$))
      .subscribe(step => {
        if (step) {
          // Update total steps from workflow
          const session = this.activeSession;
          if (session) {
            // We need to get total from workflow - for now use a workaround
            // In real implementation, we'd expose totalSteps from workflow player
          }
        }
      });

    this.workflowPlayer.error$
      .pipe(takeUntil(this.destroy$))
      .subscribe(error => {
        if (error) {
          this.addMessage('system', `Error: ${error}`);
        }
      });

    // Add welcome message
    this.addMessage('assistant', 'Hi! How can I help you today?');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Toggle widget expanded/collapsed
   */
  toggle(): void {
    if (this.isMinimized) {
      this.isMinimized = false;
      this.workflowPlayer.resumeWorkflow();
    } else {
      this.isExpanded = !this.isExpanded;
    }
  }

  /**
   * Minimize widget (keep workflow running)
   */
  minimize(): void {
    this.isMinimized = true;
    this.workflowPlayer.pauseWorkflow();
  }

  /**
   * Close widget
   */
  close(): void {
    this.isExpanded = false;
    if (this.activeSession) {
      this.workflowPlayer.cancelWorkflow('User closed widget');
    }
  }

  /**
   * Submit user query
   * Updated for unified architecture with multi-mode classification
   */
  async submitQuery(query: string): Promise<void> {
    if (!query || !query.trim() || this.isLoading || this.activeSession) {
      return;
    }

    const trimmedQuery = query.trim();
    this.userInput = '';
    this.showSuggestions = false;

    // Add user message
    this.addMessage('user', trimmedQuery);

    // Show loading
    this.isLoading = true;

    try {
      // Step 1: Classify user intent
      const intent = await this.intentClassifier.classify(trimmedQuery);

      console.log('[AI Assist] Classified intent:', intent);

      // Step 2: Handle based on mode
      if (intent.mode === 'navigate' && intent.confidence > 0.5) {
        await this.handleNavigateMode(intent, trimmedQuery);
      } else if (intent.mode === 'guide' && intent.confidence > 0.4) {
        await this.handleGuideMode(intent, trimmedQuery);
      } else if (intent.mode === 'agent' && intent.confidence > 0.5) {
        await this.handleAgentMode(intent, trimmedQuery);
      } else {
        // Low confidence - show fallback
        this.addMessage(
          'assistant',
          `I'm not sure how to help with that. Here are some things I can assist with:`
        );
        this.showSuggestions = true;
      }

    } catch (error) {
      console.error('Error processing query:', error);
      this.addMessage('system', 'Sorry, I encountered an error. Please try again.');
      this.showSuggestions = true;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Handle Navigate Mode - Quick navigation to routes
   */
  private async handleNavigateMode(intent: ClassifiedIntent, query: string): Promise<void> {
    if (!intent.targetRoute) {
      this.addMessage('assistant', `I couldn't find the page you're looking for.`);
      this.showSuggestions = true;
      return;
    }

    this.addMessage('assistant', `Navigating to ${intent.targetRoute}...`);

    // Execute navigation via ActionExecutor
    const result = await this.actionExecutor.executeIntent(intent, query, {
      mode: 'navigate'
    });

    if (result.success) {
      setTimeout(() => {
        this.addMessage('assistant', `You're now at ${intent.targetRoute}`);
        this.showSuggestions = true;
      }, 500);
    } else {
      this.addMessage('system', `Navigation failed: ${result.error}`);
      this.showSuggestions = true;
    }
  }

  /**
   * Handle Guide Mode - Step-by-step workflow guidance
   */
  private async handleGuideMode(intent: ClassifiedIntent, query: string): Promise<void> {
    try {
      // Try backend routing first (for existing workflows)
      const response = await this.apiService.routeQuery(query).toPromise();

      if (response?.workflowId && response.confidence > 0.5) {
        // Found a matching workflow
        this.addMessage('assistant', `I can help you with that. Let me guide you through the process.`);

        // Fetch workflow to get total steps
        const workflow = await this.apiService.getWorkflow(response.workflowId).toPromise();
        if (workflow) {
          this.totalSteps = workflow.steps.length;
        }

        // Start workflow after a brief delay
        setTimeout(() => {
          this.workflowPlayer.startWorkflow(response.workflowId!);
        }, 500);

      } else if (intent.targetRoute) {
        // Fallback: Navigate to the route and show generic guidance
        this.addMessage('assistant', `Let me take you there and show you around.`);
        await this.router.navigateByUrl(intent.targetRoute);
        setTimeout(() => {
          this.addMessage('assistant', `You're now at ${intent.targetRoute}. What would you like to do?`);
          this.showSuggestions = true;
        }, 500);

      } else {
        this.addMessage('assistant', response?.fallbackMessage || `I don't have a guide for that yet.`);
        this.showSuggestions = true;
      }

    } catch (error) {
      console.error('Error in guide mode:', error);
      this.addMessage('system', `Sorry, I couldn't start the guide.`);
      this.showSuggestions = true;
    }
  }

  /**
   * Handle Agent Mode - Autonomous task execution (foundation)
   */
  private async handleAgentMode(intent: ClassifiedIntent, query: string): Promise<void> {
    // Agent mode is not fully implemented yet
    this.addMessage(
      'assistant',
      `Agent mode is coming soon! For now, I can guide you through this task step-by-step. Would you like that?`
    );

    // For now, fallback to guide mode
    setTimeout(async () => {
      await this.handleGuideMode(intent, query);
    }, 1000);
  }

  /**
   * Cancel current workflow
   */
  cancelWorkflow(): void {
    this.workflowPlayer.cancelWorkflow('User cancelled');
    this.showSuggestions = true;
  }

  /**
   * Add a message to chat
   */
  private addMessage(type: 'user' | 'assistant' | 'system', text: string): void {
    const message: ChatMessage = {
      id: `msg-${Date.now()}-${Math.random()}`,
      type,
      text,
      timestamp: new Date()
    };

    this.messages.push(message);

    // Scroll to bottom after message is added
    setTimeout(() => this.scrollToBottom(), 100);
  }

  /**
   * Scroll chat to bottom
   */
  private scrollToBottom(): void {
    const chatBody = document.querySelector('.chat-body');
    if (chatBody) {
      chatBody.scrollTop = chatBody.scrollHeight;
    }
  }

  /**
   * Get CSS class for widget position
   */
  get positionClass(): string {
    return this.position;
  }

  /**
   * Check if widget has active workflow
   */
  get hasActiveWorkflow(): boolean {
    return this.activeSession?.status === 'active' || this.activeSession?.status === 'paused';
  }
}
